import React, { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./anayliticsChart.module.scss";

const getCoordinatesForPercent = (percent, radius, cx, cy) => {
  const x = cx + radius * Math.cos(2 * Math.PI * (percent - 0.25));
  const y = cy + radius * Math.sin(2 * Math.PI * (percent - 0.25));
  return [x, y];
};

const calculateDonutPath = (startPct, endPct, outerR, innerR, cx, cy) => {
  const [startX, startY] = getCoordinatesForPercent(startPct, outerR, cx, cy);
  const [endX, endY] = getCoordinatesForPercent(endPct, outerR, cx, cy);
  const [startInnerX, startInnerY] = getCoordinatesForPercent(
    startPct,
    innerR,
    cx,
    cy,
  );
  const [endInnerX, endInnerY] = getCoordinatesForPercent(
    endPct,
    innerR,
    cx,
    cy,
  );
  const largeArcFlag = endPct - startPct > 0.5 ? 1 : 0;

  return [
    `M ${startX} ${startY}`,
    `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
    `L ${endInnerX} ${endInnerY}`,
    `A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${startInnerX} ${startInnerY}`,
    "Z",
  ].join(" ");
};

const STYLES = {
  colors: {
    tooltipBg: "#2D3245",
    tooltipText: "#FFFFFF",
    text: "#333333",
  },
  chart: {
    viewBoxSize: 100,
    cx: 50,
    cy: 50,
    radius: 45,
    innerRadius: 25,
  },
};

const Tooltip = ({ x, y, data, visible }) => {
  if (!visible || !data) return null;

  return (
    <div className={styles["tooltip"]} style={{ top: y, left: x }}>
      <div className={styles["tooltip-name"]}>{data.name}</div>
      <div className={styles["tooltip-count"]}>Count: {data.value}</div>
      <div className={styles["tooltip-arrow"]} />
    </div>
  );
};

const Legend = ({ data }) => {
  return (
    <div className={styles["legend-container"]}>
      {data.map((item, i) => (
        <div key={i} className={styles["legend-item"]}>
          <div
            className={styles["legend-box"]}
            style={{ backgroundColor: item.color }}
          />
          <span>{item.name}</span>
        </div>
      ))}
    </div>
  );
};

const AnalyticalChart = ({ data, height = 500 }) => {
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const chartRef = useRef(null);

  const chartData = useMemo(() => {
    // Filter out items with zero or negative values
    const filteredData = data.filter(
      (item) => item.value != null && item.value > 0,
    );

    if (filteredData.length === 0) {
      return [];
    }

    // If only one item has a value, render it as a full circle
    if (filteredData.length === 1) {
      return [
        {
          ...filteredData[0],
          startPercent: 0,
          endPercent: 0.9999, // Full circle (use 0.9999 instead of 1 to avoid SVG path overlap issues)
        },
      ];
    }

    // Calculate percentages for multiple items
    const total = filteredData.reduce((acc, curr) => acc + curr.value, 0);
    let cumulativePercent = 0;

    return filteredData.map((item) => {
      const percent = item.value / total;
      const start = cumulativePercent;
      const end = cumulativePercent + percent;
      cumulativePercent += percent;

      return {
        ...item,
        startPercent: start,
        endPercent: end,
      };
    });
  }, [data]);

  const handleMouseMove = (e, item) => {
    setHoveredSlice(item);
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredSlice(null);
  };

  // Check if there's any valid data to display
  if (!data || data.length === 0) {
    return (
      <div
        className={` ${styles["compliance-anaylitics-chart"]} ${styles["no-data"]} `}
      >
        No Data Available
      </div>
    );
  }

  return (
    <div ref={chartRef} className={styles["compliance-anaylitics-chart"]}>
      <Legend data={data} />

      <div className={styles["svg-wrapper"]} style={{ height: height }}>
        <svg
          viewBox={`0 0 ${STYLES.chart.viewBoxSize} ${STYLES.chart.viewBoxSize}`}
          className={styles["svg-chart"]}
        >
          {chartData.map((slice, index) => {
            const pathData = calculateDonutPath(
              slice.startPercent,
              slice.endPercent,
              STYLES.chart.radius,
              STYLES.chart.innerRadius,
              STYLES.chart.cx,
              STYLES.chart.cy,
            );

            const isHovered = hoveredSlice && hoveredSlice.name === slice.name;

            return (
              <path
                key={index}
                d={pathData}
                fill={slice.color}
                onMouseMove={(e) => handleMouseMove(e, slice)}
                onMouseLeave={handleMouseLeave}
                className={styles["chart-path"]}
                style={{
                  opacity: hoveredSlice && !isHovered ? 0.6 : 1, // Dim others on hover
                }}
              />
            );
          })}
        </svg>
      </div>

      {/* Tooltip Portal */}
      <Tooltip
        visible={!!hoveredSlice}
        data={hoveredSlice}
        x={mousePos.x}
        y={mousePos.y}
      />
    </div>
  );
};

AnalyticalChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  height: PropTypes.number,
};

export default AnalyticalChart;
